/**
 * The Action Plan's view + selection state, held in the URL.
 *
 * EXTRACTED FROM `ActionPlanPanel` SO IT CAN BE TESTED AS PRODUCTION CODE.
 *
 * The first version of this behaviour was tested through a hand-written miniature that
 * re-declared the same `useSearchParams` logic inside the test file. That is negative
 * coverage: the copy can be perfectly green while the panel it claims to describe drifts
 * away from it, because nothing links the two. The logic lives here now and both the
 * panel and its tests call THIS, so a regression has nowhere to hide.
 *
 * Why the URL and not component state:
 *
 *   - selection has to survive a view switch. It previously lived in
 *     `const [, setSelectedTaskId] = useState(...)`, which discarded the value — every
 *     view called `onTaskClick={setSelectedTaskId}`, the state updated, and nothing ever
 *     read it back.
 *   - a plan view should be linkable and survive a reload.
 *
 * PARAMETER OWNERSHIP: `JourneyNavigator` owns `?tab=` and reads it back into its own
 * state. These keys are namespaced `ap*`, and every write COPIES the existing query
 * string rather than replacing it — writing a fresh `URLSearchParams` would drop `tab`,
 * and the navigator would then re-add its default, bouncing the reader out of the Action
 * Plan on their first view switch.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DEFAULT_ACTION_PLAN_VIEW, asViewMode } from '../types/actionPlan';
import type { ViewMode } from '../types/actionPlan';

/** The Action Plan's URL keys, named once so a typo cannot diverge read from write. */
export const AP_VIEW_PARAM = 'apView';
export const AP_TASK_PARAM = 'apTask';

export interface ActionPlanUrlState {
  /** The current view. An absent or unrecognised `?apView=` resolves to Matrix. */
  viewMode: ViewMode;
  /**
   * The selected task's canonical Action Plan id (`task.id` from the S3 read model), or
   * null.
   *
   * NOT a remediation candidate `stable_key`. That is source identity for a DECISION,
   * lives in a different table with a different lifetime, and is used only by the
   * Needs-review tray.
   */
  selectedTaskId: string | null;
  /** Switch view. Pushes, so Back returns to the previous view. */
  setViewMode: (mode: ViewMode) => void;
  /** Select a task, or clear the selection with `null`. Replaces rather than pushes. */
  setSelectedTaskId: (taskId: string | null) => void;
  /** Switch view AND select a task in ONE atomic write (Show on Matrix / drill). */
  setViewAndTask: (mode: ViewMode, taskId: string) => void;
  /** Write any single parameter, preserving the rest of the query string. */
  setUrlParam: (key: string, value: string | null, opts?: { replace?: boolean }) => void;
}

export function useActionPlanUrlState(): ActionPlanUrlState {
  const [searchParams, setSearchParams] = useSearchParams();

  const viewMode: ViewMode = asViewMode(searchParams.get(AP_VIEW_PARAM)) ?? DEFAULT_ACTION_PLAN_VIEW;
  const selectedTaskId = searchParams.get(AP_TASK_PARAM);

  const setUrlParam = useCallback(
    (key: string, value: string | null, opts?: { replace?: boolean }) => {
      /*
       * FUNCTIONAL UPDATER, not the captured `searchParams` (S4f-C fix). Two
       * writes in one event handler — e.g. Show on Matrix setting `apTask` and
       * then `apView` — both closed over the SAME render's params, so the second
       * write rebuilt from a snapshot that lacked the first write and silently
       * dropped it. The updater form composes sequential writes; each starts from
       * the params as they stand, not as they were when the handler rendered.
       */
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === null) next.delete(key);
          else next.set(key, value);
          return next;
        },
        { replace: opts?.replace ?? false },
      );
    },
    [setSearchParams],
  );

  const setViewMode = useCallback(
    (mode: ViewMode) => setUrlParam(AP_VIEW_PARAM, mode),
    [setUrlParam],
  );

  /*
   * Selection REPLACES rather than pushes. Clicking through tasks would otherwise fill
   * the history with entries whose only difference is which task was highlighted, and
   * Back would walk them one at a time instead of leaving the Action Plan.
   */
  const setSelectedTaskId = useCallback(
    (taskId: string | null) => setUrlParam(AP_TASK_PARAM, taskId, { replace: true }),
    [setUrlParam],
  );

  /*
   * View + selection in ONE write (S4f-C). Two setSearchParams calls in one
   * event handler cannot compose even with the functional updater: react-router
   * computes `prev` from the location as it stands at dispatch, and within one
   * tick the first navigation has not applied yet — so the second write rebuilt
   * a query string without the first's parameter and silently dropped it.
   * "Show on Matrix" and the Prism drill need both parameters to land, so they
   * land together, atomically, as one pushed history entry.
   */
  const setViewAndTask = useCallback(
    (mode: ViewMode, taskId: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(AP_VIEW_PARAM, mode);
          next.set(AP_TASK_PARAM, taskId);
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  /*
   * Align the URL with the default on first mount, with `replace` so the default does not
   * become a history entry the user can Back into. Same pattern JourneyNavigator uses for
   * `?tab=`.
   */
  const viewSyncMounted = useRef(false);
  useEffect(() => {
    if (viewSyncMounted.current) return;
    viewSyncMounted.current = true;
    if (!asViewMode(searchParams.get(AP_VIEW_PARAM))) {
      setUrlParam(AP_VIEW_PARAM, DEFAULT_ACTION_PLAN_VIEW, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- first mount only
  }, []);

  return { viewMode, selectedTaskId, setViewMode, setSelectedTaskId, setViewAndTask, setUrlParam };
}
